import { DAYS_SHORT, monthShort } from "../_lib/dates";
import { pick, type Copy, type SupplyLang } from "../_lib/lang";

/**
 * The /supply/approvals dictionary — page chrome (the rail-door links, the
 * HowToStrip mount), `view.ts`'s pure display shaping (kind/eyebrow labels,
 * meta/top-line wording, the PO-send state chips — in scope despite living
 * outside this file: `view.ts` is read ONLY by `page.tsx` and
 * `ApprovalCard.tsx`, both approvals-only, same class as waste's `data.ts`),
 * `scope.ts`'s `checkOutletScope` (in scope for the same reason — its two
 * error strings reach the user through `approveOrder`/`rejectOrder`/
 * `returnOrderToDraft`'s `setNotice`), `_components/ApprovalCard.tsx` and
 * `_components/PoSendRail.tsx` (approvals-only despite the `_components/`
 * address — grep confirms no other route imports either), `lib/procurement/
 * dispatch.ts` and `lib/procurement/whatsapp-handoff.ts` (also in scope
 * despite living in `lib/` — see the fix-round-1 note below), and
 * `actions.ts`'s own error strings. Register: everyday floor/desk BM,
 * action-first, no calques — matches copy-shared.ts's register. Trade terms
 * (CK, PO, GRN, HQ) stay identical in both languages. Approve/reject button
 * copy got an extra pass (task brief): a mistranslated approve/reject is the
 * worst possible defect on a manager decision surface.
 *
 * Fix round 1 correction: `lib/procurement/dispatch.ts` and `lib/procurement/
 * whatsapp-handoff.ts` were wrongly filed under "shared lib/ helper stays
 * English this PR" in the initial pass. That ruling covers GENUINELY
 * cross-surface infrastructure — `lib/procurement/order-status.ts` (imported
 * by order/receive/ck). It never covered `lib/inventory/waste.ts`/
 * `transfers.ts` either — those were the SAME misapplication, corrected in
 * waste/transfer's own fix round 1 (`63e51fa17`). Nor does it cover
 * `lib/inventory/grn.ts` — a whole-branch review round 2 import-graph check
 * found grn.ts is imported ONLY by `app/supply/receive/*` and
 * `lib/inventory/grn-reopen.ts`, and that import is only the non-error-
 * returning `receiptOrderStatus` status classifier and a type — never one of
 * grn.ts's SoftFail-returning functions; grn.ts's own SoftFail strings are
 * now mediated by
 * `app/supply/receive/copy.ts`'s `libGrnLineErrorCopy`/
 * `libAddAdhocGrnLineErrorCopy`, the same class of fix as the two lib files
 * below. A grep of the import graph confirms `dispatch.ts`,
 * `whatsapp-handoff.ts` and `po-channel.ts` are each imported ONLY from
 * within `app/supply/approvals/*` (including each other) — this surface's
 * own PO-send/WhatsApp-handoff logic factored into `lib/`, not shared code.
 * Their error strings ARE now mediated, string-matched against copy below by
 * `dispatchLibErrorCopy`/`whatsappHandoffLibErrorCopy` (near the bottom of
 * this file) and applied in `actions.ts` at every call site
 * (`retrySendPo`, `approveOrder`'s dispatch branch, `getWhatsAppPoDraft`,
 * `confirmWhatsAppPoSent`) BEFORE the result reaches `ApprovalCard.tsx`/
 * `PoSendRail.tsx` — mirrors `transferLibErrorCopy`'s mechanism exactly.
 * **No logic in either lib file changed** — strings only, matched and
 * substituted, nothing moved. Two classes of string still fall through
 * untranslated by design, same as the waste/transfer precedent: raw
 * Postgres error details (`claim.error.message`, `read.error.message`, …)
 * after a translated lead sentence — the `withDetail` convention every
 * surface's copy.ts already uses — and `lib/email/send.ts`'s own `reason`
 * (a deeper, genuinely shared email-provider lib) after "The email did not
 * send: ".
 *
 * Status vocabulary: `ORDER_STATUS_LABELS` (lib/procurement/order-status.ts)
 *   stays the source of exact EN wording; the StatusChip and every render
 *   surface resolve the BM half through `_lib/order-status-copy.ts` (PR4).
 *   Server actions resolve the word inside their sentences through the same
 *   module's `orderStatusStateWord` (PR5) — the chip label is a noun and
 *   would render "dah dah hantar" in these templates. The "already <status>"
 *   helpers below still translate only the surrounding sentence and pass the
 *   word through — same formula as order/copy.ts's `alreadyStatusFallback`.
 *
 * Deliberately OUT of scope:
 * - `app/supply/_lib/client-errors.ts` (`describeTransportError`) — shared
 *   cross-surface infra also mounted by RunCard, ProductionBoard and
 *   OpeningStockDesk, none of which carry `lang` yet; it is not in this
 *   task's file list either (page/queue/decision components + actions.ts),
 *   and translating one caller's transport fallback while three keep
 *   English is worse than leaving all four alone. Same class of cut as
 *   CameraStage.tsx and DeliveryNoteSheet.tsx in receive/transfer's copy.ts.
 * - The `notes` column text this file appends on reject/return
 *   ("Rejected: <reason>", "Returned for edits") — persisted data, not live
 *   chrome. It renders back through OTHER surfaces this task does not own
 *   (order/status-rail-data.ts's draft rail, the PO PDF's notes field), so
 *   translating it here would half-translate a document two surfaces render
 *   (the DeliveryNoteSheet class of cut) — and worse, it would freeze
 *   whatever language the ACTING approver had selected into a record every
 *   future reader sees regardless of their own cookie, which is wrong in the
 *   opposite direction to every live-render string in this file.
 *
 * Parameterized strings (a status label, a percentage, a line count) cannot
 * be typed as plain `Copy` — each lives as its own exported function below
 * the COPY object, taking `lang` last and returning the resolved string via
 * `pick`, same convention every other surface's copy.ts uses.
 */
export const COPY = {
  // WhatsApp group send (PR 2, 2026-09-29)
  groupReadyChip: { ms: "Sedia untuk hantar", en: "Ready to send" },
  groupCaption: { ms: "Grup WhatsApp pembekal", en: "Supplier's WhatsApp group" },
  shareYourselfInstead: { ms: "Kongsi sendiri", en: "Share it yourself instead" },
  groupWillGet: { ms: "Grup akan terima 2 mesej", en: "The group will get 2 messages" },
  groupBack: { ms: "Kembali", en: "Back" },
  groupSendNow: { ms: "Hantar sekarang", en: "Send now" },
  groupSendingButton: { ms: "Menghantar…", en: "Sending…" },
  groupStillSending: { ms: "Tengah dihantar. Kalau lebih 10 minit, tekan Cuba lagi.", en: "Being sent. If this stays for 10 minutes, tap Try again." },
  groupTryAgain: { ms: "Cuba lagi", en: "Try again" },
  groupNotSentTitle: { ms: "Tak dihantar", en: "Not sent" },
  groupMarkAsSent: { ms: "Dah ada dalam grup — tanda dihantar", en: "It's in the group — mark as sent" },
  groupRecordedByHand: { ms: "Direkod sebagai dihantar secara manual.", en: "Recorded as sent by hand." },
  groupNotPostedLine: { ms: "Tiada apa dipos dalam grup. Cuba lagi.", en: "Nothing was posted to the group. Try again." },
  groupUnsureTitle: { ms: "Tak pasti dah sampai", en: "Not sure it went" },
  groupNotThereSendAgain: { ms: "Tiada dalam grup — hantar semula", en: "It's not in the group — send again" },
  groupNotSentLine: { ms: "WhatsApp tak terima mesej ni. Tiada apa sampai ke grup.", en: "WhatsApp didn't accept the message. Nothing reached the group." },
  groupOfflineTitle: { ms: "WhatsApp ops tengah offline", en: "The ops WhatsApp is offline" },
  groupOfflineLine: { ms: "App tak boleh hantar sekarang. Kongsi PO sendiri.", en: "The app can't send right now. Share the PO yourself." },
  groupPosted: { ms: "Dah dipos dalam grup pembekal.", en: "Posted in the supplier's group." },
  groupNoteMissed: { ms: "PDF dah dihantar, tapi nota tak sampai.", en: "The PDF went, but the note didn't." },
  groupNoContact: { ms: "Tiada kontak dalam rekod, jadi tiada siapa ditag.", en: "No contact on file, so nobody is tagged." },
  groupSentToast: { ms: "PO dihantar ke grup WhatsApp.", en: "PO sent to the WhatsApp group." },
  groupSentNoteMissedToast: { ms: "PDF dihantar ke grup, tapi nota tak sampai.", en: "PDF sent to the group, but the note didn't go." },
  groupSendOff: { ms: "Hantar ke grup WhatsApp belum dibuka.", en: "Sending to WhatsApp groups is switched off." },
  groupNoGroup: { ms: "Pembekal ni tiada grup WhatsApp yang disambung.", en: "This supplier has no linked WhatsApp group." },
  groupBeforeWatch: { ms: "PO ni lebih lama dari bacaan WhatsApp untuk grup ni. Kongsi sendiri kalau masih perlu.", en: "This PO is older than the WhatsApp reader for this group. Share it yourself if it still needs sending." },
  groupAlreadyInGroup: { ms: "PO ni dah ada dalam grup pembekal.", en: "This PO is already in the supplier's group." },
  groupAlreadySent: { ms: "PO ni dah dihantar.", en: "This PO has already been sent." },
  groupInProgress: { ms: "PO ni tengah dihantar — tunggu sekejap.", en: "This PO is being sent — wait a moment." },
  groupChanged: { ms: "PO ni dah berubah. Tengok semula sebelum hantar.", en: "This PO changed. Check it again before sending." },
  groupRefused: { ms: "WhatsApp tak terima mesej ni. Cuba lagi, atau kongsi sendiri.", en: "WhatsApp didn't accept the message. Try again, or share it yourself." },
  groupNotSure: { ms: "Tak pasti PO dah sampai. Tengok grup dulu sebelum cuba lagi.", en: "We can't tell if it went. Check the group before trying again." },
  groupNotApproved: { ms: "Hanya PO yang diluluskan boleh dihantar.", en: "Only an approved PO can be sent." },
  groupPdfFailed: { ms: "Dokumen PO tak dapat disediakan. Cuba lagi.", en: "The PO document could not be made. Try again." },
  groupEmailRecord: { ms: "PO ni dah ada rekod hantar melalui emel.", en: "This PO already has an email send record." },

  // (The nine *RailLink desk-door entries that used to open this object were
  // removed 2026-08-18 with the page's link stack — the rail, module tabs and
  // More sheet carry those labels via NAV_COPY now.)

  // ── view.ts — order-kind / detail labels ────────────────────────────────
  // Matches order/copy.ts's draftKindLabel/vendorPoLabel roots exactly, so
  // the same doc-kind reads identically on both surfaces.
  ckRequisitionKindLabel: { ms: "Permintaan", en: "Requisition" },
  vendorPoKindLabel: { ms: "PO Pembekal", en: "Vendor PO" },
  ckRequisitionEyebrow: { ms: "Permintaan", en: "Requisition" },
  vendorPoEyebrow: { ms: "Pesanan pembelian", en: "Purchase order" },
  supplierFromLabel: { ms: "Pembekal", en: "Supplier" },
  outletFromLabel: { ms: "Dari", en: "From" },
  centralKitchenDeliverTo: { ms: "Dapur Pusat", en: "Central Kitchen" },
  vendorFallback: { ms: "Pembekal", en: "Vendor" },
  // Audit finding 17: the rule is two-PERSON, not HQ — an outlet manager who
  // could legally clear it was told to wait for HQ; and at zero signatures
  // "tandatangan kedua" implied someone had already signed.
  needsTwoSignOffsChip: { ms: "Perlu 2 tandatangan", en: "Needs 2 sign-offs" },
  secondSignOffNeededChip: { ms: "Perlu tandatangan orang kedua", en: "Needs a second person's sign-off" },
  sentFallback: { ms: "Dihantar", en: "Sent" },

  // ── ApprovalCard / ApprovalQueue ────────────────────────────────────────
  allClearTitle: { ms: "Semua settle", en: "All clear" },
  unpricedLabel: { ms: "— harga belum ditetapkan", en: "— unpriced" },
  backToApprovalsButton: { ms: "Kelulusan", en: "Approvals" },
  // Elevated queue (2026-08-23): hero = the oldest order awaiting a decision.
  nextUpLabel: { ms: "Seterusnya", en: "Next up" },
  awaitingApprovalChip: { ms: "Tunggu kelulusan", en: "Pending approval" },
  reviewAndApproveButton: { ms: "Semak & luluskan", en: "Review & approve" },
  waitingLabel: { ms: "Menunggu", en: "Waiting" },
  lineUnit: { ms: "baris", en: "line" },
  linesUnit: { ms: "baris", en: "lines" },
  unpricedShort: { ms: "harga belum ditetapkan", en: "unpriced" },
  deliverToLabel: { ms: "Hantar ke", en: "Deliver to" },
  neededByChipLabel: { ms: "Perlu", en: "Needed by" },
  neededByNotSet: { ms: "Belum tetap", en: "Not set" },
  statusLabel: { ms: "Status", en: "Status" },
  nothingWaitingTitle: { ms: "Tiada apa-apa tunggu kelulusan", en: "Nothing waiting for approval" },
  reasonPlaceholder: {
    ms: "Kenapa awak tolak order ni? Outlet akan nampak nota ni.",
    en: "Why are you rejecting this order? The outlet will see this note.",
  },
  keepOrderButton: { ms: "Kekalkan order", en: "Keep order" },
  rejectingButton: { ms: "Menolak…", en: "Rejecting…" },
  rejectOrderButton: { ms: "Tolak order", en: "Reject order" },
  approvingButton: { ms: "Meluluskan…", en: "Approving…" },
  giveFirstSignOffButton: { ms: "Beri tandatangan pertama", en: "Give first sign-off" },
  approveButton: { ms: "Lulus", en: "Approve" },
  workingButton: { ms: "Sedang proses…", en: "Working…" },
  editLinesButton: { ms: "Edit baris", en: "Edit lines" },
  rejectButton: { ms: "Tolak", en: "Reject" },
  couldNotSaveTryAgain: { ms: "Tak dapat simpan — cuba lagi.", en: "Could not save — try again." },

  // ── PoSendRail ───────────────────────────────────────────────────────
  vendorPosHeading: { ms: "PO Pembekal", en: "Vendor POs" },
  unknownRaiser: { ms: "Pembuat order tak diketahui", en: "Unknown raiser" },
  hqOwnershipHint: {
    ms: "HQ tambah nombor WhatsApp atau emel pembekal (tanya Shaq).",
    en: "HQ adds the supplier's WhatsApp number or email (ask Shaq).",
  },
  pdfLabel: { ms: "PDF", en: "PDF" },
  preparingButton: { ms: "Menyediakan…", en: "Preparing…" },
  shareButton: { ms: "Kongsi", en: "Share" },
  downloadLink: { ms: "Muat turun", en: "Download" },
  sendingButton: { ms: "Menghantar…", en: "Sending…" },
  retryingButton: { ms: "Mencuba semula…", en: "Retrying…" },
  sendButton: { ms: "Hantar", en: "Send" },
  retrySendButton: { ms: "Cuba hantar semula", en: "Retry send" },
  whatsappLabel: { ms: "WhatsApp", en: "WhatsApp" },
  preparingPoButton: { ms: "Menyediakan PO…", en: "Preparing PO…" },
  sharePoWhatsappButton: { ms: "Kongsi PO ke WhatsApp", en: "Share PO to WhatsApp" },
  openTheirChatButton: { ms: "Buka chat diorang sebaliknya", en: "Open their chat instead" },
  sentMarkPoNote: {
    ms: "Dah hantar? Tekan ni untuk tanda PO dah dihantar. App tak ada cara lain nak tahu.",
    en: "Sent it? Tap this to mark the PO as sent. The app has no other way to know it went out.",
  },
  recordingButton: { ms: "Merekod…", en: "Recording…" },
  iSentItButton: { ms: "Saya dah hantar", en: "I sent it" },
  noChannelChip: { ms: "Tiada nombor WhatsApp atau emel disimpan", en: "No WhatsApp number or email saved" },
  whatsappReadyChip: { ms: "Serahan WhatsApp", en: "WhatsApp handoff" },
  // Audit finding 11: the approve toast on this branch was the bare chip noun
  // above — it read as "sent" while ~26 live POs sat un-shared. The toast now
  // says approved AND not-sent AND where the share lives.
  whatsappApprovedToast: { ms: "Diluluskan — PO belum dihantar lagi. Kongsi ke WhatsApp dari senarai \"PO Pembekal\" kat halaman ini.", en: "Approved — the PO has not gone out yet. Share it to WhatsApp from the \"PO Pembekal\" list on this page." },
  notSentChip: { ms: "Tiada penghantaran direkod", en: "No send recorded" },
  sendingChip: { ms: "Menghantar", en: "Sending" },
  sendFailedPrefix: { ms: "Hantar gagal", en: "Send failed" },
  unknownSendError: { ms: "Ralat hantar tak diketahui", en: "Unknown send error" },
  poEmailedToast: { ms: "PO diemel kepada pembekal.", en: "PO emailed to the supplier." },
  couldNotSendTryAgain: { ms: "Tak dapat hantar PO — cuba lagi.", en: "Could not send the PO — try again." },
  couldNotSendRefresh: {
    ms: "Tak dapat hantar PO — segar semula dan cuba lagi.",
    en: "Could not send the PO — refresh and retry.",
  },
  pdfReadyToShare: { ms: "PDF PO sedia untuk dikongsi.", en: "PO PDF ready to share." },
  sharingUnavailableDownloaded: {
    ms: "Kongsi tak tersedia — PDF dimuat turun.",
    en: "Sharing is unavailable — PDF downloaded.",
  },
  pickWhatsappSupplierChat: {
    ms: "Pilih WhatsApp, lepas tu chat pembekal.",
    en: "Pick WhatsApp, then the supplier's chat.",
  },
  sharingUnavailableAttach: {
    ms: "Kongsi tak tersedia kat sini — PDF dimuat turun, attach dalam WhatsApp.",
    en: "Sharing is unavailable here — the PDF downloaded, attach it in WhatsApp.",
  },
  chatNotOpened: {
    ms: "WhatsApp tak terbuka — telefon mungkin sekat. Tekan \"Kongsi PO ke WhatsApp\" untuk hantar PDF.",
    en: "WhatsApp did not open — your phone may have blocked it. Tap \"Share PO to WhatsApp\" to send the PDF instead.",
  },
  orderNotCopied: {
    ms: "Order tak dapat disalin. Tekan \"Kongsi PO ke WhatsApp\" untuk hantar PDF.",
    en: "The order could not be copied. Tap \"Share PO to WhatsApp\" to send the PDF instead.",
  },
  orderCopiedTooLong: {
    ms: "Order disalin — terlalu panjang nak isi automatik. Paste dalam WhatsApp.",
    en: "Order copied — it is too long to fill in for you. Paste it into WhatsApp.",
  },
  recordedSentWhatsapp: { ms: "Direkod sebagai dihantar di WhatsApp.", en: "Recorded as sent on WhatsApp." },
  // Edit before sending (2026-09-17): the door on each unsent row…
  editBeforeSendingLink: { ms: "Ubah sebelum hantar", en: "Edit before sending" },
  // …and the PDF that was shared is no longer the PO.
  poChangedShareAgain: {
    ms: "PO ni dah berubah selepas awak buka. Kongsi PDF yang baru, lepas tu tekan \"Saya dah hantar\".",
    en: "This PO changed after you opened it. Share the new PDF, then confirm.",
  },
  poShareBeforeConfirm: {
    ms: "Kongsi PDF PO dulu, lepas tu tekan \"Saya dah hantar\".",
    en: "Share the PO PDF first, then tap \"I sent it\".",
  },
  couldNotRecordIt: { ms: "Tak dapat rekod.", en: "Could not record it." },

  // ── PoSendRail — cancel lane ──────────────────────────────────────────
  cancelPoButton: { ms: "Batal PO", en: "Cancel PO" },
  cancellingButton: { ms: "Membatalkan…", en: "Cancelling…" },
  cancelReasonPlaceholder: {
    ms: "Kenapa awak batal PO ni? Pembekal tak akan tahu secara automatik.",
    en: "Why are you cancelling this PO? The supplier is not told automatically.",
  },
  poCancelledToast: { ms: "PO dibatalkan.", en: "PO cancelled." },
  poCancelledTellSupplierToast: {
    ms: "PO dibatalkan — tapi PO ni dah pun sampai ke pembekal. Beritahu diorang yang PO dah batal.",
    en: "PO cancelled — but this PO already went to the supplier. Tell them it's cancelled.",
  },

  // ── actions.ts (gate + validation + soft errors) ──────────────────────
  orderingOff: { ms: "Order belum dihidupkan lagi.", en: "Ordering is not switched on yet." },
  vendorOrderingOff: {
    ms: "Order pembekal belum dihidupkan lagi.",
    en: "Vendor ordering is not switched on yet.",
  },
  poSendNotAllowed: {
    ms: "PO ni hanya boleh dihantar oleh orang yang buat order atau pelulus.",
    en: "Only this order's raiser or an approver can send this PO.",
  },
  // These four + the three "X failed" keys below are byte-pinned by
  // actions.test.ts/two-stage-actions.test.ts against today's EN literals:
  // lowercase, no trailing period. Do not "fix" the casing.
  orderIdRequired: { ms: "id order diperlukan", en: "order id required" },
  orderReadFailed: { ms: "baca order gagal", en: "order read failed" },
  orderNotFound: { ms: "order tak jumpa", en: "order not found" },
  unrecognisedOrderRow: { ms: "baris order tak dikenali", en: "unrecognised order row" },
  orderChangedRefresh: {
    ms: "order dah berubah — segar semula dan cuba lagi",
    en: "order changed — refresh and retry",
  },
  noOutletCannotApprove: {
    ms: "Order ni tiada outlet — tak boleh lulus di sini.",
    en: "This order has no outlet — it cannot be approved here.",
  },
  noOutletCannotReject: {
    ms: "Order ni tiada outlet — tak boleh tolak di sini.",
    en: "This order has no outlet — it cannot be rejected here.",
  },
  noOutletCannotReturn: {
    ms: "Order ni tiada outlet — tak boleh pulangkan di sini.",
    en: "This order has no outlet — it cannot be returned here.",
  },
  // ORPHANED ON PURPOSE, 2026-08-24: self-approval is allowed (Shaq), so this
  // key has no consumers. Kept, not deleted, so reversing that decision is a
  // one-line change rather than a re-translation. Do not "tidy" it away.
  selfApprovalBlocked: {
    ms: "Awak yang hantar order ni - orang lain kena lulus.",
    en: "You submitted this order - someone else has to approve it",
  },
  // Plain hyphen kept on purpose in BOTH languages — two-stage-actions.test.ts
  // pins this EN string byte-for-byte with the ASCII hyphen the original code
  // always used, not an em dash. (The same note used to cover
  // selfApprovalBlocked above; nothing pins that one any more.)
  gaveFirstSignOff: {
    ms: "Awak dah beri tandatangan pertama - orang lain kena beri yang kedua.",
    en: "You gave the first sign-off - someone else has to give the second.",
  },
  couldNotCheckSpendLimit: {
    ms: "Tak dapat semak had belanja — cuba lagi.",
    en: "Could not check the spend limit — try again.",
  },
  couldNotCheckSignOffs: {
    ms: "Tak dapat semak tandatangan sebelum ni — cuba lagi.",
    en: "Could not check earlier sign-offs — try again.",
  },
  bothSignOffsSamePerson: {
    ms: "Kedua-dua tandatangan pada order ni dari orang yang sama — pulangkan untuk edit dan lulus semula.",
    en: "Both sign-offs on this order are from the same person — send it back for edits and approve it again.",
  },
  someoneElseGaveSecondSignOff: {
    ms: "Orang lain dah beri tandatangan kedua — minta diorang tekan Lulus semula.",
    en: "Someone else has already given the second sign-off — ask them to tap Approve again.",
  },
  noOutletCannotCancel: {
    ms: "Order ni tiada outlet — tak boleh batal di sini.",
    en: "This order has no outlet — it cannot be cancelled here.",
  },
  stillPendingUseReject: {
    ms: "Order ni masih tunggu kelulusan — guna Tolak dalam senarai kelulusan.",
    en: "This order is still waiting for approval — use Reject in the approvals list instead.",
  },
  receivingStartedCannotCancel: {
    ms: "Penerimaan dah mula untuk order ni — habiskan atau batalkan penghantaran tu dulu, lepas tu cuba lagi.",
    en: "Receiving has already started for this order — finish or cancel that delivery first, then try again.",
  },
  kitchenSentCannotCancel: {
    ms: "Dapur dah hantar order ni — tak boleh batal. Terima barang dan tolak mana yang salah.",
    en: "The kitchen has already sent this order — it cannot be cancelled. Receive the delivery and reject anything that is wrong.",
  },
  goodsReceivedCannotCancel: {
    ms: "Barang order ni dah diterima — penghantaran dah difailkan, jadi order ni tak boleh dibatalkan. Kalau ni nampak tak betul, beritahu HQ.",
    en: "Goods from this order were already received — a delivery was filed, so it cannot be cancelled. If that looks wrong, tell HQ.",
  },
  couldNotCheckDeliveries: {
    ms: "Tak dapat semak penghantaran order ni — cuba lagi.",
    en: "Could not check this order's deliveries — try again.",
  },
  approveFailed: { ms: "lulus gagal", en: "approve failed" },
  reasonRequired: { ms: "sebab diperlukan", en: "reason required" },
  rejectFailed: { ms: "tolak gagal", en: "reject failed" },
  cancelFailed: { ms: "batal gagal", en: "cancel failed" },
  returnForEditsFailed: { ms: "pulangkan untuk edit gagal", en: "return for edits failed" },
  couldNotClearSignOffs: {
    ms: "Tak dapat kosongkan tandatangan sebelum ni — cuba lagi.",
    en: "Could not clear the earlier sign-offs — try again.",
  },
  noSupplierOnOrder: { ms: "Pesanan pembelian ni tiada pembekal.", en: "This purchase order has no supplier." },
  supplierNotWhatsApp: {
    ms: "Pembekal ni belum disediakan untuk order WhatsApp.",
    en: "This supplier is not set up for WhatsApp orders.",
  },

  // ── scope.ts (checkOutletScope) ────────────────────────────────────────
  noApprovalAccessOutlet: {
    ms: "Awak tiada akses kelulusan untuk outlet ni.",
    en: "You do not have approval access for this outlet.",
  },
  belongsToAnotherOutlet: {
    ms: "Order ni milik outlet lain — manager diorang sendiri yang lulus.",
    en: "This order belongs to another outlet — its own manager approves it.",
  },

  // ── lib/procurement/dispatch.ts (fix round 1: mediated via
  //    dispatchLibErrorCopy below, NOT a change to dispatch.ts) ───────────
  dispatchOnlyVendorPo: {
    ms: "Cuma PO pembekal boleh dihantar.",
    en: "Only vendor purchase orders can be dispatched.",
  },
  dispatchUnrecognisedOrderStatus: { ms: "Status order tak dikenali.", en: "Unrecognised order status." },
  noChannelMessage: {
    ms: "Tiada nombor WhatsApp atau emel disimpan untuk pembekal ni — tambah satu, lepas tu hantar.",
    en: "No WhatsApp number or email on file for this supplier — add one, then send.",
  },
  couldNotClaimEmailSendLead: { ms: "Tak dapat tuntut penghantaran emel", en: "Could not claim the email send" },
  existingSendRecordUnreadableRetry: {
    ms: "Rekod penghantaran sedia ada tak dapat dibaca — segar semula dan cuba lagi.",
    en: "The existing send record could not be read — refresh and retry.",
  },
  sendAlreadyInProgress: {
    ms: "Penghantaran sedang berjalan — segar semula sekejap lagi.",
    en: "A send is already in progress — refresh in a moment.",
  },
  existingSendRecordUnrecognisedStatus: {
    ms: "Rekod penghantaran sedia ada ada status tak dikenali.",
    en: "The existing send record has an unrecognised status.",
  },
  couldNotRetryEmailSendLead: {
    ms: "Tak dapat cuba semula penghantaran emel",
    en: "Could not retry the email send",
  },
  anotherRetryClaimedSend: {
    ms: "Cubaan semula lain dah tuntut penghantaran ni — segar semula sekejap lagi.",
    en: "Another retry claimed this send — refresh in a moment.",
  },
  emailDidNotSendLead: { ms: "Emel tak berjaya hantar", en: "The email did not send" },
  dispatchFailedLead: { ms: "Hantar gagal", en: "Dispatch failed" },

  // ── lib/procurement/whatsapp-handoff.ts (fix round 1: mediated via
  //    whatsappHandoffLibErrorCopy below, NOT a change to whatsapp-handoff.ts) ─
  onlyVendorPoCanBeSentToSupplier: {
    ms: "Cuma PO pembekal boleh dihantar kepada pembekal.",
    en: "Only vendor purchase orders can be sent to a supplier.",
  },
  existingSendRecordUnreadableTryAgain: {
    ms: "Rekod penghantaran sedia ada tak dapat dibaca — segar semula dan cuba lagi.",
    en: "The existing send record could not be read — refresh and try again.",
  },
  poAlreadyHasEmailSendRecord: {
    ms: "PO ni dah ada rekod penghantaran dari emel. Minta HQ kosongkan dulu sebelum rekod penghantaran WhatsApp.",
    en: "This PO already has a send record from email. Ask HQ to clear it before recording a WhatsApp send.",
  },
  couldNotRecordSendLead: { ms: "Tak dapat rekod penghantaran", en: "Could not record the send" },
  orderCouldNotBeReadTryAgain: {
    ms: "Order tak dapat dibaca — segar semula dan cuba lagi.",
    en: "The order could not be read — refresh and try again.",
  },
  orderNotFoundCapitalized: { ms: "Order tak jumpa.", en: "Order not found." },
  // Quoted price on a PO line (2026-09-06). Plain words: the person is a chef
  // or manager on a phone; the numbers do the talking.
  quoteVendorOnly: { ms: "Harga sebut hanya untuk PO pembekal", en: "Quoted prices are for supplier POs only" },
  quoteNotYetSubmitted: { ms: "Hantar order dulu — harga sebut ditambah selepas itu", en: "Submit the order first — quoted prices are added after that" },
  quoteAfterSend: { ms: "PO dah dihantar — harga tak boleh diubah lagi", en: "This PO has been sent — the price can no longer be changed" },
  quoteAfterReceiving: {
    ms: "Barang untuk PO ni dah mula diterima — harga tak boleh diubah lagi",
    en: "Receiving has started for this PO — the price can no longer be changed",
  },
  quoteLineNotFound: { ms: "Baris tak jumpa — refresh", en: "Line not found — refresh" },
  quoteFormatError: { ms: "Masukkan harga lebih dari RM 0.00 (paling banyak 4 titik perpuluhan)", en: "Enter a price above RM 0.00 (at most 4 decimal places)" },
  quoteOverCeiling: { ms: "Harga terlalu tinggi — had RM {ceiling} setiap unit", en: "Price too high — the ceiling is RM {ceiling} per unit" },
  quoteOutsideBand: { ms: "Pembekal senaraikan RM {listing} setiap {pack}. Harga mesti antara RM {low} dan RM {high}, atau semak semula", en: "The supplier lists RM {listing} per {pack}. The price must be between RM {low} and RM {high} — check the figure" },
  quoteOverLimitApproved: { ms: "Harga ini bawa order melebihi RM {limit}. Order ini dah diluluskan dengan satu tandatangan — minta pengurus batalkan dan buat order baru supaya dua orang tandatangan", en: "This price takes the order over RM {limit}. It was approved on one signature — ask a manager to cancel it and raise it again so two people sign" },
  quoteClearOverLimitApproved: {
    ms: "Kalau harga ni dibuang, harga senarai yang lebih tinggi dipakai dan order jadi melebihi RM {limit}. Order ini dah diluluskan dengan satu tandatangan — minta pengurus batalkan dan buat order baru supaya dua orang tandatangan",
    en: "Removing this price brings back the higher list price and takes the order over RM {limit}. It was approved on one signature — ask a manager to cancel it and raise it again so two people sign",
  },
  quoteTooSmall: { ms: "Harga terlalu kecil untuk pek ini — setiap unit asas jadi RM 0.0000", en: "Price too small for this pack — it rounds to RM 0.0000 per base unit" },
  quoteWriteFailed: { ms: "Harga tak tersimpan — cuba lagi", en: "Price not saved — try again" },
  quoteLabel: { ms: "Harga sebut", en: "Quoted price" },
  quoteHint: { ms: "RM setiap {unit}", en: "RM per {unit}" },
  quoteSave: { ms: "Simpan harga", en: "Save price" },
  quoteClear: { ms: "Buang", en: "Clear" },
  quoteSaved: { ms: "Harga sebut disimpan", en: "Quoted price saved" },
  quoteCleared: { ms: "Harga sebut dibuang", en: "Quoted price cleared" },
  quotedChip: { ms: "SEBUT", en: "QUOTED" },
  quotePackWord: { ms: "sepek", en: "pack of" },
  estimatedTotalSuffix: { ms: "(anggaran)", en: "(est.)" },
} satisfies Record<string, Copy>;

export function poOutstandingLabel(
  raiserName: string,
  ageDays: number,
  lang: SupplyLang,
): string {
  if (ageDays < 1) {
    return lang === "ms"
      ? `${raiserName} · diluluskan hari ni`
      : `${raiserName} · approved today`;
  }
  return lang === "ms"
    ? `${raiserName} · diluluskan ${ageDays} hari lepas`
    : `${raiserName} · approved ${ageDays} ${ageDays === 1 ? "day" : "days"} ago`;
}

// ── parameterized helpers ──────────────────────────────────────────────

/** The generic "already <status>" fallback (assertTransition can fail
 *  against any status in the machine) — the label arrives as the caller
 *  resolved it, today `_lib/order-status-copy`'s `orderStatusStateWord`, so
 *  both halves of the sentence speak one language. Mirrors order/copy.ts's
 *  alreadyStatusFallback exactly. */
export function alreadyLabel(labelLower: string, lang: SupplyLang): string {
  return lang === "ms" ? `dah ${labelLower}` : `already ${labelLower}`;
}

const ALREADY_PREFIX: Record<SupplyLang, string> = { ms: "dah ", en: "already " };

/** Detects `alreadyLabel()`'s shape for the CURRENT lang and returns the
 *  extracted status label, or null when `error` is some other string
 *  entirely (a DB failure, a validation message, …). Matching against the
 *  current lang's own prefix — not a hardcoded English one — is what lets
 *  this keep working once the server starts returning BM error text. */
export function parseAlreadyLabel(error: string, lang: SupplyLang): string | null {
  const prefix = ALREADY_PREFIX[lang];
  return error.startsWith(prefix) ? error.slice(prefix.length) : null;
}

/** Client-side wrapper (ApprovalCard's describeSoftError): the server's
 *  short `already <label>` form expands into a full, friendly sentence. The
 *  label itself is already resolved in the reader's language by the server
 *  (`orderStatusStateWord`), so only the wrapper varies here. */
export function alreadyChangedBySomeoneElse(labelLower: string, lang: SupplyLang): string {
  return lang === "ms"
    ? `Order ni dah ditukar oleh orang lain — sekarang ${labelLower}. Menyegar semula senarai.`
    : `This order was already changed by someone else — it is ${labelLower}. Refreshing the list.`;
}

export function couldNotSaveDetail(detail: string, lang: SupplyLang): string {
  return lang === "ms" ? `Tak dapat simpan — ${detail}.` : `Could not save — ${detail}.`;
}

export function supplierChannelReadFailed(detail: string, lang: SupplyLang): string {
  return lang === "ms"
    ? `Baca saluran pembekal gagal: ${detail}`
    : `Supplier channel read failed: ${detail}`;
}

/** "N line" / "N lines" — BM does not inflect for plural. */
export function lineCountLabel(n: number, lang: SupplyLang): string {
  return lang === "ms" ? `${n} baris` : `${n} ${n === 1 ? "line" : "lines"}`;
}

export function metaWithNeededBy(lineCount: string, neededByLabel: string, lang: SupplyLang): string {
  return lang === "ms" ? `${lineCount} · perlu ${neededByLabel}` : `${lineCount} · needed ${neededByLabel}`;
}

export function orderTotalMeta(lineCount: number, lang: SupplyLang): string {
  const countLabel = lineCountLabel(lineCount, lang);
  return lang === "ms" ? `Jumlah order · ${countLabel}` : `Order total · ${countLabel}`;
}

export function openLabel(docCode: string, lineCount: number, lang: SupplyLang): string {
  return lang === "ms"
    ? `Buka ${docCode} — semua ${lineCount} baris`
    : `Open ${docCode} — all ${lineCount} lines`;
}

export function returnedToDraftToast(routeName: string, lang: SupplyLang): string {
  return lang === "ms" ? `Dipulangkan ke ${routeName} sebagai draf.` : `Returned to ${routeName} as a draft.`;
}

/** The lead words before EmptyQueue's TermTip element ("New " in EN; empty
 *  in BM, where the equivalent word lands AFTER the term instead). */
export function newRequisitionsLead(lang: SupplyLang): string {
  return lang === "ms" ? "" : "New ";
}

/** The trailing sentence after EmptyQueue's TermTip element — BM's word
 *  order puts "baharu" (new) here, right after the term. */
export function newRequisitionsTail(lang: SupplyLang): string {
  return lang === "ms"
    ? " baharu muncul di sini bila outlet hantar."
    : " appear here when outlets submit them.";
}

/** ListRow detail for a queued (non-hero) order: "<kind> · RM 1,234.00" or
 *  "<kind> · harga belum ditetapkan". Hard-capped at 40 chars (one 11px line on a
 *  390px row beside a badge and a chevron) — the kind label gives way first,
 *  the number never does. */
export const ROW_DETAIL_MAX = 40;
export function queueRowDetail(kindLabel: string, totalLabel: string | null, lang: SupplyLang): string {
  const money = totalLabel ?? pick(lang, COPY.unpricedShort);
  const full = `${kindLabel} · ${money}`;
  if (full.length <= ROW_DETAIL_MAX) return full;
  const room = ROW_DETAIL_MAX - money.length - 4;
  return room > 0 ? `${kindLabel.slice(0, room)}… · ${money}` : money.slice(0, ROW_DETAIL_MAX);
}

/** Hero meta: at most TWO facts, ≤40 chars, the date never truncated —
 *  "6 baris · perlu 9 Ogos" / "6 lines · needed 9 Aug" (BM month names come
 *  whole from _lib/dates.ts). `neededBy` is day+short month (no weekday) or
 *  null; if
 *  even two facts overflow, the date fact alone survives. */
export function heroMeta(lineCount: number, neededBy: string | null, lang: SupplyLang): string {
  const lines = lineCountLabel(lineCount, lang);
  if (!neededBy) return lines;
  const full = lang === "ms" ? `${lines} · perlu ${neededBy}` : `${lines} · needed ${neededBy}`;
  return full.length <= ROW_DETAIL_MAX ? full : (lang === "ms" ? `perlu ${neededBy}` : `needed ${neededBy}`);
}

export function waitingCountTitle(n: number, lang: SupplyLang): string {
  return lang === "ms" ? `${n} tunggu` : `${n} waiting`;
}

export function rejectDocLabel(docCode: string, lang: SupplyLang): string {
  return lang === "ms" ? `Tolak ${docCode}` : `Reject ${docCode}`;
}

export function cancelDocLabel(docCode: string, lang: SupplyLang): string {
  return lang === "ms" ? `Batal ${docCode}` : `Cancel ${docCode}`;
}

export function needsFirstSignOffOrTotal(totalLabel: string | null, lang: SupplyLang): string {
  return totalLabel
    ? (lang === "ms" ? `Lulus ${totalLabel}` : `Approve ${totalLabel}`)
    : pick(lang, COPY.approveButton);
}

export function firstSignOffRecordedToast(lang: SupplyLang): string {
  return lang === "ms"
    ? "Tandatangan pertama direkod — approver lain kena tandatangan sebelum order ni diluluskan."
    : "First sign-off recorded — a second approver has to sign off before this order is approved.";
}

export function approvedEmailedToast(lang: SupplyLang): string {
  return lang === "ms" ? "Diluluskan — PO diemel kepada pembekal." : "Approved — PO emailed to the supplier.";
}

export function approvedDispatchFailedToast(reasonSentence: string, tail: string, lang: SupplyLang): string {
  return lang === "ms" ? `Diluluskan. ${reasonSentence}${tail}` : `Approved. ${reasonSentence}${tail}`;
}

export function approvedGenericFailToast(lang: SupplyLang): string {
  return lang === "ms"
    ? "Diluluskan. Emel tak berjaya hantar — cuba semula dari senarai PO Pembekal."
    : "Approved. Email did not send — retry from the Vendor POs list.";
}

export function retryFromVendorPosTail(lang: SupplyLang): string {
  return lang === "ms" ? " Cuba semula dari senarai PO Pembekal." : " Retry from the Vendor POs list.";
}

/** Sent-label formatter — "Sent" alone, "Sent 10:42" (today) or
 *  "Sent Fri 7 Aug" (older). The clock/date text itself stays en-GB
 *  formatted (same convention every surface's Intl date output keeps). */
export function sentAtLabel(dateText: string | null, lang: SupplyLang): string {
  const fallback = pick(lang, COPY.sentFallback);
  if (!dateText) return fallback;
  return `${fallback} ${dateText}`;
}

// ── fix round 1: lib/procurement/dispatch.ts + whatsapp-handoff.ts
//    mediators. Neither lib file changed — every raw string it returns is
//    exact/prefix-matched here and swapped for translated copy; an
//    unrecognised string (a future lib change) falls through untouched
//    rather than silently dropping the message. Mirrors
//    transfer/copy.ts's transferLibErrorCopy exactly. ─────────────────────

const ORDER_IS_SENT_SUFFIX = " — only approved orders can be sent.";
const ORDER_IS_RECORDED_SENT_SUFFIX = " — only approved orders can be recorded as sent.";
const ORDER_IS_PREFIX = "Order is ";

/** Extracts the status label out of "Order is <label><suffix>",
 *  or null when `raw` isn't that shape. `raw` is a server-action error, so
 *  the label inside it is the EN lib word today (see module doc); only the
 *  surrounding sentence translates. */
function extractOrderIsLabel(raw: string, suffix: string): string | null {
  if (raw.startsWith(ORDER_IS_PREFIX) && raw.endsWith(suffix)) {
    return raw.slice(ORDER_IS_PREFIX.length, raw.length - suffix.length);
  }
  return null;
}

export function orderIsStatusOnlyApprovedSent(label: string, lang: SupplyLang): string {
  return lang === "ms"
    ? `Order dalam status ${label} — cuma order yang diluluskan boleh dihantar.`
    : `Order is ${label} — only approved orders can be sent.`;
}

export function orderIsStatusOnlyApprovedRecordedSent(label: string, lang: SupplyLang): string {
  return lang === "ms"
    ? `Order dalam status ${label} — cuma order yang diluluskan boleh direkod sebagai dihantar.`
    : `Order is ${label} — only approved orders can be recorded as sent.`;
}

/** "<lead>: <detail>" — DB/provider detail stays English after a
 *  translated lead, the same `withDetail` convention order/receive already
 *  use. Returns null when `raw` doesn't start with `leadEn + ": "`. */
function withLibLead(raw: string, leadEn: string, leadCopy: Copy, lang: SupplyLang): string | null {
  const prefix = `${leadEn}: `;
  if (!raw.startsWith(prefix)) return null;
  return `${pick(lang, leadCopy)}: ${raw.slice(prefix.length)}`;
}

const SUPPLIER_CHANNEL_PREFIX = "Supplier channel read failed: ";

/** Both dispatch.ts and whatsapp-handoff.ts compose this exact lead over
 *  `resolvePoChannel`'s (po-channel.ts) raw DB detail — reuse the
 *  already-shipped `supplierChannelReadFailed` mediator (used directly by
 *  `confirmWhatsAppPoSent` in actions.ts) rather than a second copy of the
 *  same lead text. */
function extractSupplierChannelDetail(raw: string): string | null {
  return raw.startsWith(SUPPLIER_CHANNEL_PREFIX) ? raw.slice(SUPPLIER_CHANNEL_PREFIX.length) : null;
}

/**
 * `dispatchVendorPo`/`loadOrder` (lib/procurement/dispatch.ts) stay
 * untouched — the strings they return are exact/prefix-matched here.
 * `NO_CHANNEL_MESSAGE` (lib/procurement/po-channel.ts) is included too:
 * dispatch.ts is its only caller that surfaces it to the user.
 */
export function dispatchLibErrorCopy(raw: string, lang: SupplyLang): string {
  if (raw === "Only vendor purchase orders can be dispatched.") return pick(lang, COPY.dispatchOnlyVendorPo);
  if (raw === "Unrecognised order status.") return pick(lang, COPY.dispatchUnrecognisedOrderStatus);
  if (raw === "This purchase order has no supplier.") return pick(lang, COPY.noSupplierOnOrder);
  if (raw === "No WhatsApp number or email on file for this supplier — add one, then send.") {
    return pick(lang, COPY.noChannelMessage);
  }
  if (raw === "The existing send record could not be read — refresh and retry.") {
    return pick(lang, COPY.existingSendRecordUnreadableRetry);
  }
  if (raw === "A send is already in progress — refresh in a moment.") {
    return pick(lang, COPY.sendAlreadyInProgress);
  }
  if (raw === "The existing send record has an unrecognised status.") {
    return pick(lang, COPY.existingSendRecordUnrecognisedStatus);
  }
  if (raw === "Another retry claimed this send — refresh in a moment.") {
    return pick(lang, COPY.anotherRetryClaimedSend);
  }
  if (raw === "order read failed") return pick(lang, COPY.orderReadFailed);
  if (raw === "order not found") return pick(lang, COPY.orderNotFound);

  const sentLabel = extractOrderIsLabel(raw, ORDER_IS_SENT_SUFFIX);
  if (sentLabel !== null) return orderIsStatusOnlyApprovedSent(sentLabel, lang);

  const channelDetail = extractSupplierChannelDetail(raw);
  if (channelDetail !== null) return supplierChannelReadFailed(channelDetail, lang);

  const withDetail =
    withLibLead(raw, "Could not claim the email send", COPY.couldNotClaimEmailSendLead, lang) ??
    withLibLead(raw, "Could not retry the email send", COPY.couldNotRetryEmailSendLead, lang) ??
    withLibLead(raw, "The email did not send", COPY.emailDidNotSendLead, lang) ??
    withLibLead(raw, "Dispatch failed", COPY.dispatchFailedLead, lang);
  if (withDetail !== null) return withDetail;

  return raw;
}

/**
 * `buildWhatsAppDraft`/`confirmWhatsAppSent` (lib/procurement/
 * whatsapp-handoff.ts) stay untouched — same exact/prefix-match mechanism as
 * `dispatchLibErrorCopy`. `resolvePoChannel`'s own DB-detail passthrough
 * (`channel.error`) is covered by the shared "Supplier channel read failed"
 * lead both files return verbatim.
 */
export function whatsappHandoffLibErrorCopy(raw: string, lang: SupplyLang): string {
  if (raw === "This PO is being sent to the WhatsApp group — wait a moment.") return pick(lang, COPY.groupInProgress);
  if (raw === "Only vendor purchase orders can be sent to a supplier.") {
    return pick(lang, COPY.onlyVendorPoCanBeSentToSupplier);
  }
  if (raw === "This purchase order has no supplier.") return pick(lang, COPY.noSupplierOnOrder);
  if (raw === "This supplier is not set up for WhatsApp orders.") return pick(lang, COPY.supplierNotWhatsApp);
  if (raw === "The existing send record could not be read — refresh and try again.") {
    return pick(lang, COPY.existingSendRecordUnreadableTryAgain);
  }
  if (raw === "This PO already has a send record from email. Ask HQ to clear it before recording a WhatsApp send.") {
    return pick(lang, COPY.poAlreadyHasEmailSendRecord);
  }
  if (raw === "The order could not be read — refresh and try again.") {
    return pick(lang, COPY.orderCouldNotBeReadTryAgain);
  }
  if (raw === "Order not found.") return pick(lang, COPY.orderNotFoundCapitalized);
  if (raw === "Unrecognised order status.") return pick(lang, COPY.dispatchUnrecognisedOrderStatus);

  const sentLabel = extractOrderIsLabel(raw, ORDER_IS_SENT_SUFFIX);
  if (sentLabel !== null) return orderIsStatusOnlyApprovedSent(sentLabel, lang);
  const recordedLabel = extractOrderIsLabel(raw, ORDER_IS_RECORDED_SENT_SUFFIX);
  if (recordedLabel !== null) return orderIsStatusOnlyApprovedRecordedSent(recordedLabel, lang);

  const channelDetail = extractSupplierChannelDetail(raw);
  if (channelDetail !== null) return supplierChannelReadFailed(channelDetail, lang);

  const withDetail = withLibLead(raw, "Could not record the send", COPY.couldNotRecordSendLead, lang);
  if (withDetail !== null) return withDetail;

  return raw;
}

export function sendToGroupLabel(label: string, lang: SupplyLang): string {
  return lang === "ms" ? `Hantar ke ${label}` : `Send to ${label}`;
}
export function sentToGroupLabel(label: string, lang: SupplyLang): string {
  return lang === "ms" ? `Dihantar ke ${label}` : `Sent to ${label}`;
}
export function groupCaptionLine(caption: string, lang: SupplyLang): string {
  return lang === "ms" ? `kapsyen: ${caption}` : `caption: ${caption}`;
}
export function groupReplyingTo(fileName: string, lang: SupplyLang): string {
  return lang === "ms" ? `Balas kepada ${fileName}` : `Replying to ${fileName}`;
}
export function groupTagsContact(name: string, lang: SupplyLang): string {
  return lang === "ms" ? `Tag ${name}, kontak pembekal dalam rekod.` : `Tags ${name}, the supplier's contact on file.`;
}
/** "Mon 28 Sep, 13:08 · by A. L. I" in MYT (+8 h, then UTC getters). No name ⇒ the stamp alone. */
export function groupSentLine(iso: string, name: string | null, lang: SupplyLang): string {
  const t = Date.parse(iso);
  if (!Number.isFinite(t)) return "";
  const d = new Date(t + 8 * 60 * 60 * 1000);
  const hh = String(d.getUTCHours()).padStart(2, "0");
  const mm = String(d.getUTCMinutes()).padStart(2, "0");
  const stamp = `${DAYS_SHORT[lang][d.getUTCDay()]} ${d.getUTCDate()} ${monthShort(d.getUTCMonth(), lang)}, ${hh}:${mm}`;
  if (!name) return stamp;
  return lang === "ms" ? `${stamp} · oleh ${name}` : `${stamp} · by ${name}`;
}

export function groupUnsureLine(label: string, lang: SupplyLang): string {
  return lang === "ms"
    ? `WhatsApp tak jawab. Tengok ${label}: kalau PDF PO dah ada, tekan "Saya dah hantar". Kalau tiada, hantar semula.`
    : `WhatsApp didn't answer. Check ${label}: if the PO PDF is there, tap "I sent it". If it's not, send again.`;
}

export function groupAlreadyInGroupTitle(label: string, lang: SupplyLang): string {
  return lang === "ms" ? `Dah ada dalam ${label}` : `Already in ${label}`;
}

export function groupPostedLine(iso: string, name: string | null, lang: SupplyLang): string {
  const stamp = groupSentLine(iso, null, lang);
  if (!stamp) return "";
  return lang === "ms" ? `Dipos ${stamp} oleh ${name ?? "seseorang"}` : `Posted ${stamp} by ${name ?? "someone"}`;
}
